import {
  Ellipsis,
  GenericTable,
  GenericTableVariant,
  NewButton,
  NewButtonSize,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { getDocumentIcon } from "components/Modal/ChooseFilesModal/utils";
import { Trash2 } from "lucide-react";
import { arrayOf, shape } from "prop-types";
import { memo, useMemo } from "react";
import { useDispatch } from "react-redux";

import { setFiles } from "../../../../slices/workspace/chooseFilesModalSlice";
import style from "../Browser/CollectionsPanel.module.scss";
import { fileShape } from "../propTypes";
import classes from "./DetailsTable.module.scss";

const FILE_COL_SIZE = 200;
const SOURCE_COL_SIZE = 90;
const DELETE_COL_SIZE = 45;

const getColumns = (dispatch, selectedFiles) => [
  {
    header: "File Name",
    accessorKey: "name",
    id: "name",
    size: FILE_COL_SIZE,
    minSize: FILE_COL_SIZE,
    cell: ({ row }) => {
      const file = row.original;
      return (
        <span className="choose_files_modal__body__details__file_name">
          {getDocumentIcon(file.content.name)}
          <Ellipsis text={file.content.name} />
        </span>
      );
    },
    sortingFn: (a, b) =>
      a.original.content.name.localeCompare(b.original.content.name, "en", {
        numeric: true,
        sensitivity: "base",
      }),
  },
  {
    header: "Source",
    accessorKey: "source",
    id: "source",
    size: SOURCE_COL_SIZE,
    minSize: SOURCE_COL_SIZE,
    cell: ({ row }) => {
      const file = row.original;
      return (
        <span className="choose_files_modal__body__details__file_source">
          <Ellipsis text={file.source} />
        </span>
      );
    },
    sortingFn: (a, b) => b.original.source.localeCompare(a.original.source),
  },
  {
    header: "",
    accessorKey: "delete",
    id: "delete",
    enableSorting: false,
    enableResizing: false,
    size: DELETE_COL_SIZE,
    minSize: DELETE_COL_SIZE,
    pin: "right",
    cell: ({ row }) => {
      const onClickDelete = () => {
        dispatch(
          setFiles(
            selectedFiles.filter((f) => {
              // If both have IDs, compare IDs
              if (f.id && row.original.id) {
                return f.id !== row.original.id;
              }
              // If no IDs, fall back to hash comparison
              return f.hash !== row.original.hash;
            })
          )
        );
      };
      return (
        <NewButton
          className={"delete_btn"}
          variant={NewButtonVariant.red}
          size={NewButtonSize.sm}
          onClick={onClickDelete}
          leftIcon={
            <Trash2 className={style["trash"]} height={16} width={16} />
          }
        />
      );
    },
  },
];

const DetailsTable = ({ selectedFiles }) => {
  const dispatch = useDispatch();

  const columns = useMemo(
    () => getColumns(dispatch, selectedFiles),
    [selectedFiles]
  );

  const handleSort = (sortedFiles) => {
    dispatch(setFiles(sortedFiles));
  };

  return (
    <GenericTable
      className={classes.table}
      cellClassName={classes.cell}
      headerCellClassName={classes.headerCell}
      data={selectedFiles}
      columns={columns}
      loading={false}
      defaultSortColumnIndex={0}
      rowIdKey="hash"
      selectedIndex={-1}
      onSort={handleSort}
      variant={GenericTableVariant.bordered}
    />
  );
};

export default memo(DetailsTable);
DetailsTable.propTypes = {
  selectedFiles: arrayOf(shape(fileShape)).isRequired,
};
